import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API_BASE,
  headers: {
    'Content-Type': 'application/json',
  },
});

api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Don't auto-logout if offline - let AuthContext handle it
    if (!navigator.onLine) {
      return Promise.reject(error);
    }
    
    // Only handle 401 if we're online and it's a real auth error
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authApi = {
  login: (email, password) => api.post('/auth/login', { email, password }),
  register: (email, password, name, company) => 
    api.post('/auth/register', { email, password, name, company }),
  getMe: () => api.get('/auth/me'),
  requestPasswordReset: (email) => api.post('/auth/password-reset-request', { email }),
  changePassword: (currentPassword, newPassword) => 
    api.put('/auth/change-password', { current_password: currentPassword, new_password: newPassword }),
};

// Users API (Admin)
export const usersApi = {
  getAll: (status = null) => api.get('/users', { params: status ? { status } : {} }),
  getPending: () => api.get('/users/pending'),
  approve: (userId, role, status) => api.put(`/users/${userId}/approve`, { role, status }),
  delete: (userId) => api.delete(`/users/${userId}`),
  resetPassword: (userId, newPassword) => api.put(`/admin/users/${userId}/reset-password`, { new_password: newPassword }),
  getPasswordResetRequests: () => api.get('/admin/password-reset-requests'),
  deletePasswordResetRequest: (requestId) => api.delete(`/admin/password-reset-requests/${requestId}`),
  // Admin creates user
  createUser: (data) => api.post('/admin/users/create', data),
  getUserLimitInfo: () => api.get('/admin/user-limit'),
};

// Checklists API
export const checklistsApi = {
  getAll: (buildingType = null, inspectionType = null) => {
    const params = {};
    if (buildingType) params.building_type = buildingType;
    if (inspectionType) params.inspection_type = inspectionType;
    return api.get('/checklists', { params });
  },
  getById: (id) => api.get(`/checklists/${id}`),
  // Admin functions
  create: (data) => api.post('/admin/checklists', data),
  update: (id, data) => api.put(`/admin/checklists/${id}`, data),
  delete: (id) => api.delete(`/admin/checklists/${id}`),
  duplicate: (id) => api.post(`/admin/checklists/${id}/duplicate`),
  export: (id) => api.get(`/admin/checklists/${id}/export`),
  import: (data) => api.post('/admin/checklists/import', data),
};

// Inspections API
export const inspectionsApi = {
  getAll: (status = null) => 
    api.get('/inspections', { params: status ? { status } : {} }),
  getById: (id) => api.get(`/inspections/${id}`),
  create: (data) => api.post('/inspections', data),
  update: (id, data) => api.put(`/inspections/${id}`, data),
  delete: (id) => api.delete(`/inspections/${id}`),
  complete: (id) => api.post(`/inspections/${id}/complete`),
  updateItem: (inspectionId, itemId, data) => 
    api.put(`/inspections/${inspectionId}/items/${itemId}`, data),
  uploadPhoto: async (inspectionId, itemId, file) => {
    const formData = new FormData();
    formData.append('photo', file);
    return api.post(`/inspections/${inspectionId}/items/${itemId}/photos`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  deletePhoto: (inspectionId, itemId, photoIndex) => 
    api.delete(`/inspections/${inspectionId}/items/${itemId}/photos/${photoIndex}`),
  exportPDF: (id) => api.get(`/inspections/${id}/pdf`, { responseType: 'blob' }),
  addSignature: (inspectionId, signatureData) => 
    api.post(`/inspections/${inspectionId}/signature`, signatureData),
  deleteSignature: (inspectionId, role) => 
    api.delete(`/inspections/${inspectionId}/signature/${role}`),
  sendEmail: (inspectionId, data) =>
    api.post(`/inspections/${inspectionId}/send-email`, data),
  getEmailLog: (inspectionId) =>
    api.get(`/inspections/${inspectionId}/email-log`),
  // Mittlere Höhe Extras API
  getMittlereHoeheExtras: (inspectionId) =>
    api.get(`/inspections/${inspectionId}/mittlere-hoehe-extras`),
  updateMittlereHoeheExtras: (inspectionId, data) =>
    api.put(`/inspections/${inspectionId}/mittlere-hoehe-extras`, { mittlere_hoehe_extras: data }),
};

// E-Mail API
export const emailApi = {
  getStatus: () => api.get('/email/status'),
};

// Dashboard API
export const dashboardApi = {
  getStats: () => api.get('/dashboard/stats'),
  getDetailedStats: () => api.get('/dashboard/detailed-stats'),
  exportExcel: () => api.get('/statistics/export-excel', { responseType: 'blob' }),
};

// Building & Inspection Types API
export const typesApi = {
  getBuildingTypes: () => api.get('/building-types'),
  getInspectionTypes: () => api.get('/inspection-types'),
};

export default api;
